'use client';

// imports
import { useAtom } from "jotai";
import { useState, useRef, useEffect } from "react";

// lib files
import { appSignInAtom, PageStatusEnum } from "@/lib/atoms/app-sign-in";
import { isAlphaNumeric } from "@/lib/helpers/isAlphaNumeric";
import { useHasMounted } from '@/lib/hooks';

// svgs
import CloseIcon from '@/public/svg/close.svg';

// styles
import styles from './AuthCodeInput.module.scss';

const AUTH_CODE_MIN_LENGTH = 6;
const AUTH_CODE_MAX_LENGTH = 7;

enum AuthCodeErrorMessages {
  PASSPORT_TOKEN_ERROR = "It looks like you are trying to enter a Passport activation code. To activate Passport membership, visit <a href='https://www.pbs.org/passport'>pbs.org/passport</a>",
  INVALID_LENGTH = `Sign in code must be ${AUTH_CODE_MIN_LENGTH} or ${AUTH_CODE_MAX_LENGTH} characters long.`,
  ALPHANUMERIC_ONLY = "Empty spaces or special characters are not allowed.",
}

// Business rule: If the user enters a value that contains 4 sets of alpha characters separated by dashes ("-"), it looks like a passport code.
const isLikePassportToken = (str: string) =>{
    var split = str.split('-');
    return split.length === 4;
}

const AuthCodeInput = () => {
  const [ authCodeInputIsAtMinLength, setAuthCodeInputIsAtMinLength ] = useState(false);
  const [ appSignInState, setAppSignInState ] = useAtom(appSignInAtom);

  const inputRef = useRef<HTMLInputElement>(null);
  const hasMounted = useHasMounted();

  // support for the ?authCode= query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // only get the specified query param
    const authCodeQueryParam = params.get("authCode");

    if (
        hasMounted &&
        inputRef.current &&
        authCodeQueryParam &&
        isAlphaNumeric(authCodeQueryParam) &&
        authCodeQueryParam.length >= AUTH_CODE_MIN_LENGTH &&
        authCodeQueryParam.length <= AUTH_CODE_MAX_LENGTH
    ) {
        setAuthCodeInputIsAtMinLength(true);
        inputRef.current.value = authCodeQueryParam;
    }
  }, [hasMounted]);

  useEffect(() => {
    if (hasMounted && inputRef.current && appSignInState.authCode !== null) {
      inputRef.current.value = appSignInState.authCode;
      setAuthCodeInputIsAtMinLength(appSignInState.authCode.length >= AUTH_CODE_MIN_LENGTH);
    }
  }, [hasMounted, appSignInState.authCode]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const authCode = (e.target as HTMLFormElement).authCode.value;

    // Client side validation
    switch(true) {
      // Does this look like a passport token?
      case isLikePassportToken(authCode):
        setAppSignInState((prevState) => ({
          ...prevState,
          authCodeError: AuthCodeErrorMessages.PASSPORT_TOKEN_ERROR,
        }));
        break;
      // Does it contain special characters?
      case !isAlphaNumeric(authCode):
        setAppSignInState((prevState) => ({
          ...prevState,
          authCodeError: AuthCodeErrorMessages.ALPHANUMERIC_ONLY,
        }));
        break;
      // Is it not the correct length?
      case authCode.length < AUTH_CODE_MIN_LENGTH || authCode.length > AUTH_CODE_MAX_LENGTH:
        setAppSignInState((prevState) => ({
          ...prevState,
          authCodeError: AuthCodeErrorMessages.INVALID_LENGTH,
        }));
        break;
      // If none of the above, submit the authCode
      default:
        setAppSignInState((prevState) => ({
          ...prevState,
          authCode: authCode,
          authCodeError: null,
          pageStatus: PageStatusEnum.authCodeSubmitLoading,
        }));
        break;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAuthCodeInputIsAtMinLength(e.target.value.length >= AUTH_CODE_MIN_LENGTH);
  };

  if (!hasMounted) {
    return null;
  }

  return (
    <div className={styles.auth_code_input}>
      <h1 className={styles.auth_code_input__headline}>Sign Into Your PBS App</h1>
      <p className={styles.auth_code_input__text}>
        Enter the code displayed on your TV screen to sign into the PBS app on one of the following devices:
      </p>
      <ul className={styles.auth_code_input__device_list}>
          <li>Roku</li>
          <li>Apple TV</li>
          <li>Amazon Fire TV</li>
          <li>Android TV</li>
          <li>LG TV</li>
          <li>VIZIO SmartCast TV</li>
          <li>Samsung Smart TV</li>
          <li>Comcast Xfinity</li>
      </ul>

      <form action="" onSubmit={handleSubmit} className={styles.auth_code_input__form}>
        <label htmlFor="authCode" className="visuallyhidden">Sign In Code</label>
        <input
          type="text"
          name="authCode"
          id="authCode"
          required
          placeholder="Sign In Code"
          onChange={handleChange}
          ref={inputRef}
          className={styles.auth_code_input__input}
          // Note: the autoComplete attribute does _not_ prevent browsers from/
          // trying to autofill the input.
          autoComplete="off"
        />
        <button
          type="submit"
          disabled={!authCodeInputIsAtMinLength}
          className={styles.auth_code_input__button}
        >
          Continue
        </button>

        {appSignInState.authCodeError && (
          <p
            className={styles.auth_code_error}
          >
          <CloseIcon />
          {appSignInState.authCodeError}
          </p>
        )}

      </form>

    </div>
  );
};

export default AuthCodeInput;
